import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Popup for assigning bin to putaway task.
 */
const AssignBinPopUp = ({
  open = false,
  onClose,
  onApply,
  putawayTask = null,
  bins = [],
  loading = false,
}) => {
  const [selectedBinId, setSelectedBinId] = useState('');

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      setSelectedBinId(''); // Reset on close
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleApply = () => {
    if (selectedBinId && onApply) {
      onApply(selectedBinId);
    }
  };

  if (!open || !putawayTask) return null;

  const popupContent = (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-bin-popup-title"
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with cross */}
        <div className="flex items-center justify-between px-6 pt-4">
          <h3 id="assign-bin-popup-title" className="text-lg font-semibold text-gray-900">
            Assign Bin to Putaway
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-2">
          {/* Putaway Task Info */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs font-semibold text-gray-700 mb-2">Putaway Task Details:</p>
            <div className="space-y-1 text-xs text-gray-600">
              <div><strong>Putaway ID:</strong> {putawayTask.putawayId}</div>
              <div><strong>Product:</strong> {putawayTask.productId?.sku || 'N/A'} - {putawayTask.productId?.description || 'N/A'}</div>
              {putawayTask.batchId && (
                <div><strong>Batch:</strong> {putawayTask.batchId?.batchNumber || 'N/A'}</div>
              )}
              <div><strong>Quantity:</strong> {putawayTask.quantity}</div>
            </div>
          </div>

          {/* Bin Selection */}
          <div className="mb-4">
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
              <strong>Required:</strong> {putawayTask.quantity} unit(s). Only bins that have this product are shown.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Bin *
            </label>
            <select
              value={selectedBinId}
              onChange={(e) => setSelectedBinId(e.target.value)}
              className="w-full h-12 rounded-lg border border-gray-300 bg-white px-4 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-200"
            >
              <option value="">-- Select Bin --</option>
              {bins.length === 0 ? (
                <option value="" disabled>No bins found for this product</option>
              ) : (
                bins.map((bin) => {
                  const available = Math.max(0, (bin.capacity || 0) - (bin.currentQuantity ?? 0));
                  const hasCapacity = available >= (putawayTask.quantity || 0);
                  return (
                    <option key={bin._id} value={bin._id}>
                      {bin.binCode} ({bin.binType}) — Free: {available} / {bin.capacity || 0}
                      {!hasCapacity ? ' — Please make free space in this bin' : ''}
                    </option>
                  );
                })
              )}
            </select>
            <p className="mt-1.5 text-xs text-gray-500">
              Only product-specific bins shown. If no space, please make free space in that bin.
            </p>
          </div>
        </div>

        {/* Footer - Cancel / Apply */}
        <div className="flex items-center justify-end gap-3 px-6 pb-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-full border border-gray-300 bg-white px-5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={!selectedBinId || loading}
            className="h-10 rounded-full border border-emerald-500 bg-emerald-500 px-5 text-sm font-medium text-white hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default AssignBinPopUp;
